import React, { type CSSProperties } from 'react'
import { css, type CSSObject } from '@emotion/react'
import styled from '@emotion/styled'
import { LoadingLockupTextBase } from '@instacart/ids-core'
import { colors } from '../../../foundation/colors'
import { fontWeight } from '../../../foundation/fontWeight'
import { spacing } from '../../../foundation/spacing'
import { GRID_ROW_HEIGHT, GRID_FONT_SIZE, GRID_HEADER_ROW_HEIGHT } from './grid.constants'

const sharedCellStyles = css({
  fontSize: GRID_FONT_SIZE,
  color: colors.GRAYSCALE.X70,
  verticalAlign: 'middle',
  paddingLeft: spacing.X8,
  paddingRight: spacing.X8,
  paddingTop: spacing.X4,
  paddingBottom: spacing.X4,
  display: 'flex',
  flexDirection: 'row',
  flexWrap: 'nowrap',
  alignItems: 'center',
  whiteSpace: 'nowrap',
  textOverflow: 'ellipsis',
  minWidth: 0,
  backgroundColor: colors.GRAYSCALE.X0,
})

export const gridBodyCellStyles = css(sharedCellStyles, {})

export const gridHeaderCellStyles = css(sharedCellStyles, {
  alignContent: 'left',
  fontWeight: fontWeight.SEMIBOLD,
  whiteSpace: 'normal',
})

export const gridSortableCellStyles = css({
  borderLeft: `1px solid transparent`,
  borderRight: `1px solid transparent`,
})

export const gridSortedCellStyles = css({
  borderLeft: `1px solid ${colors.GRAYSCALE.X30}`,
  borderRight: `1px solid ${colors.GRAYSCALE.X30}`,
})

export const gridSortedHeaderCellStyles = css({
  borderTop: `1px solid ${colors.GRAYSCALE.X30}`,
})

const sharedRowStyles = css({
  height: GRID_ROW_HEIGHT,
  verticalAlign: 'middle',
  borderBottomWidth: 1,
})

export const gridBodyRowStyles = css(sharedRowStyles, {
  borderBottomStyle: 'solid',
  borderBottomColor: colors.GRAYSCALE.X20,
  backgroundColor: colors.GRAYSCALE.X0,
})

export const gridHeaderRowStyles = css(sharedRowStyles, {
  height: GRID_HEADER_ROW_HEIGHT,
  borderBottomWidth: 1,
  borderBottomStyle: 'solid',
  borderBottomColor: colors.GRAYSCALE.X20,
})

export const gridLoadingRowStyles = css(sharedRowStyles, {
  paddingLeft: spacing.X8,
  paddingRight: spacing.X16,
  paddingTop: spacing.X12,
  paddingBottom: spacing.X16,
})

export const gridOuterStyles = css({
  minWidth: '100%',
  backgroundColor: colors.GRAYSCALE.X0,
  display: 'flex',
  flexDirection: 'column',
  flexWrap: 'nowrap',
})

const sharedBodyContentStyles: CSSProperties = {
  overflowY: 'scroll',
  borderBottomWidth: 1,
  borderBottomStyle: 'solid',
  borderBottomColor: colors.GRAYSCALE.X20,
}

// Is a style object prop instead of an emotion css object
export const windowStyleOverrides: CSSProperties = {
  ...sharedBodyContentStyles,
}

export const windowInnerStyle = {
  display: 'table',
  height: '100%',
  width: '100%',
}

export const gridErrorMessageStyles = css({
  ...(sharedBodyContentStyles as CSSObject),
  height: '100%',
})

export const gridEmptyStateStyles = css({
  ...(sharedBodyContentStyles as CSSObject),
  height: '100%',
})

export const GridStickyRow = styled.div<{ index: number }>(
  {
    position: 'sticky',
    zIndex: 4,
    left: 0,
    width: '100%',
    height: GRID_HEADER_ROW_HEIGHT,
  },
  ({ index }) => ({
    top: index * GRID_HEADER_ROW_HEIGHT,
  })
)

export const GridRowsContainer = styled.div<{ rowOffset: number }>(
  {
    position: 'relative',
  },
  ({ rowOffset }) => ({ paddingTop: GRID_HEADER_ROW_HEIGHT * rowOffset })
)

export const GridCellLoading = () => (
  <div css={{ flex: 1 }} data-testid="grid-loading-cell">
    <LoadingLockupTextBase css={{ height: spacing.X28 }} />
  </div>
)
